import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { API_URL_V4 } from 'src/environments/environment.local';
import * as filterHelpers from '../helpers/filter.helpers';
import { Sport } from '../models/sport';

import { BrowseFilter } from '../models/filter';
import { ApiHttpResponse, VideoSection } from '@kinomap-dev/ng-ui-lib';
import { VideoApi } from 'app/modules/dashboard/api/video.api';
import { ExternalVideoApi } from 'app/modules/dashboard/api/external-video.api';
import { PlaylistApi } from 'app/modules/dashboard/api/playlist.api';

@Injectable({ providedIn: 'root' })
export class MapApi {
  private http: HttpClient = inject(HttpClient);
  private videoApi: VideoApi = inject(VideoApi);
  private externalVideoApi: ExternalVideoApi = inject(ExternalVideoApi);
  private playlistApi: PlaylistApi = inject(PlaylistApi);

  private readonly videosUrl: string = `${API_URL_V4}/scenic`;
  private params: HttpParams = new HttpParams().set('locationType', 'city');
  private limit: number = 50; // TODO de base c'était à 200 mais c'est hyper long ...
  private currentPage: number = 0;
  isLastPageLoaded: boolean = false;

  getVideos$({
    sport,
    filters,
    latitude,
    longitude,
    radius,
    isNextPage,
    id,
    path,
  }: {
    sport: Sport;
    filters: BrowseFilter[];
    latitude: number;
    longitude: number;
    radius: number;
    isNextPage: boolean;
    id?: number;
    path?: 'playlist' | 'myVideos' | 'videos';
  }): Observable<{ videos: VideoSection[]; total: number }> {
    const pageToAsk: number = isNextPage ? this.currentPage + 1 : 1;
    this.currentPage = pageToAsk;

    switch (path) {
      case 'myVideos': {
        this.params = this.params
          .set('page', pageToAsk)
          .set('limit', this.limit)
          .set('withoutCoaching', 'true');

        this.setFilterParams(filters);

        return this.videoApi
          .getVideos(pageToAsk, this.limit, null, this.params)
          .pipe(
            map((res: ApiHttpResponse<VideoSection[]>) => this.mapResponse(res))
          );
      }
      case 'videos': {
        this.setFilterParams(filters);

        return this.externalVideoApi
          .getVideos(pageToAsk, this.limit, id, this.params)
          .pipe(
            map((res: ApiHttpResponse<VideoSection[]>) => this.mapResponse(res))
          );
      }
      case 'playlist': {
        // 🎵 Mode Playlist
        this.setFilterParams(filters);

        return this.playlistApi
          .getVideoFromPlaylist(id, pageToAsk, this.params)
          .pipe(
            map((res: ApiHttpResponse<VideoSection[]>) => this.mapResponse(res))
          );
      }
      default: {
        // 🗺️ Mode World Map (par défaut)
        this.params = this.params
          .set('sport', sport.name)
          .set('location', `[${latitude},${longitude}]`)
          .set('radius', radius)
          .set('limit', this.limit)
          .set('page', pageToAsk);

        this.setFilterParams(filters);

        return this.http
          .get(
            `${this.videosUrl}?${decodeURIComponent(this.params.toString())}`
          )
          .pipe(
            map((res: any) => {
              this.currentPage = +res.meta.current_page;
              this.isLastPageLoaded = this.currentPage === +res.meta.last_page;

              return {
                videos: res.data,
                total: res.meta.total,
              };
            })
          );
      }
    }
  }

  private mapResponse(res: ApiHttpResponse<VideoSection[]>): {
    videos: VideoSection[];
    total: number;
  } {
    this.currentPage = +res.meta.current_page;
    this.isLastPageLoaded = this.currentPage === +res.meta.last_page;

    return {
      videos: res.data,
      total: res.meta.total,
    };
  }

  private setFilterParams(filters: BrowseFilter[]): void {
    this.setDurationParams(filters);
    this.setDistanceParams(filters);
    this.setSlopeParams(filters);
    this.setDifficultiesParam(filters);
    this.setQualitiesParam(filters);
    this.setProgressParams(filters);
    this.setSoundParams(filters);
    this.setSpeedParams(filters);
    this.setPowerParams(filters);
    this.setTagParam(filters);
    this.setInAppParam(filters);
    this.setClimbCategoriesParam(filters);
    this.setRegionsParams(filters);
    // this.setVehiclesParam(filters);
  }

  private setDurationParams(filters: BrowseFilter[]): void {
    const [min, max] = filterHelpers.getDurationParams(filters);
    this.params = this.params.set('minDuration', min).set('maxDuration', max);
  }

  private setDistanceParams(filters: BrowseFilter[]): void {
    const [min, max] = filterHelpers.getDistanceParams(filters);
    this.params = this.params.set('minDistance', min).set('maxDistance', max);
  }

  private setSlopeParams(filters: BrowseFilter[]): void {
    const [min, max] = filterHelpers.getSlopeParams(filters);
    this.params = this.params
      .set('minPositiveSlope', min)
      .set('maxPositiveSlope', max);
  }

  private setDifficultiesParam(filters: BrowseFilter[]): void {
    const diffParam: string[] = filterHelpers.getDifficultiesParam(filters);
    if (this.params.has('difficulties[]')) {
      this.params = this.params.delete('difficulties[]');
    }
    diffParam.forEach((difficulty: string) => {
      this.params = this.params.append('difficulties[]', difficulty);
    });
  }

  private setQualitiesParam(filters: BrowseFilter[]): void {
    const qualitiesParams: string[] = filterHelpers.getQualitiesParam(filters);
    if (this.params.has('qualities[]')) {
      this.params = this.params.delete('qualities[]');
    }
    qualitiesParams.forEach((quality: string) => {
      this.params = this.params.append('qualities[]', quality);
    });
  }

  private setProgressParams(filters: BrowseFilter[]): void {
    const { excludeComplete, excludeStarted } =
      filterHelpers.getProgressParam(filters);
    this.params = this.params
      .set('excludeComplete', excludeComplete)
      .set('excludeStarted', excludeStarted);
  }

  private setSoundParams(filters: BrowseFilter[]): void {
    const soundParam: string = filterHelpers.getSoundParam(filters);
    this.params = this.params.set('soundAvailable', soundParam);
  }

  private setSpeedParams(filters: BrowseFilter[]): void {
    const [min, max] = filterHelpers.getSpeedParams(filters);
    this.params = this.params.set('minAvgSpeed', min).set('maxAvgSpeed', max);
  }

  private setPowerParams(filters: BrowseFilter[]): void {
    const [min, max] = filterHelpers.getPowerParams(filters);
    this.params = this.params.set('minAvgPower', min).set('maxAvgPower', max);
  }

  private setTagParam(filters: BrowseFilter[]): void {
    const tagParam: number[] = filterHelpers.getTagParam(filters);
    tagParam.forEach((tagId: number) => {
      this.params = this.params.append('tagIds[]', tagId);
    });
  }

  private setInAppParam(filters: BrowseFilter[]): void {
    const inAppParam: string = filterHelpers.getInAppParam(filters);
    this.params = this.params.set('inApp', inAppParam);
  }

  private setClimbCategoriesParam(filters: BrowseFilter[]): void {
    const climbCategoriesParam: number[] =
      filterHelpers.getClimbCategoriesParam(filters);
    if (this.params.has('climbCategories[]')) {
      this.params = this.params.delete('climbCategories[]');
    }
    climbCategoriesParam.forEach((climbCategory: number) => {
      this.params = this.params.append(
        'climbCategories[]',
        climbCategory.toString()
      );
    });
  }

  private setRegionsParams(filters: BrowseFilter[]): void {
    const regionParam: string[] = filterHelpers.getRegionParam(filters);
    if (this.params.has('codeCountry[]')) {
      this.params = this.params.delete('codeCountry[]');
    }
    regionParam.forEach((region: string) => {
      this.params = this.params.append('codeCountry[]', region);
    });
  }

  // private setVehiclesParam(filters: BrowseFilter[]): void {
  //   const vehiclesParam: string[] = filterHelpers.getVehiclesParam(filters);
  //   if (this.params.has('vehicles[]')) {
  //     this.params = this.params.delete('vehicles[]');
  //   }
  //   vehiclesParam.forEach((vehicle: string) => {
  //     this.params = this.params.append('vehicles[]', vehicle);
  //   });
  // }
}
